import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminEditPressMedia() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [media, setMedia] = useState('');
  const [link, setLink] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPressMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/api/admin/press-media/${id}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        const item = response.data.pressMedia;
        setTitle(item.title);
        setMedia(item.media);
        setLink(item.link || '');
        setExistingImage(item.imagepath);
      } else {
        setError(response.data.message || 'Failed to fetch press & media item.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching press & media item.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPressMedia();
  }, [fetchPressMedia]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('media', media);
    formData.append('link', link);
    if (image) formData.append('image', image);

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        setSaving(false);
        return;
      }

      const response = await axios.put(`${apiUrl}/api/admin/press-media/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        setSuccess('Press & media item updated successfully.');
        setTimeout(() => navigate('/admin-press-media'), 1200);
      } else {
        setError(response.data.message || 'Failed to update press & media item.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating press & media item.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <Link to="/admin-press-media" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Press & Media
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Edit</span>
                </li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Edit Press & Media Item</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        Image
                      </label>
                      <div className="d-flex align-items-center gap-16">
                        <img
                          src={imagePreview || (existingImage ? `${apiUrl}/${existingImage}` : '')}
                          alt="preview"
                          style={{
                            width: 100, height: 80, borderRadius: 12, objectFit: 'cover',
                            border: '3px solid #e8f5ee',
                            display: (imagePreview || existingImage) ? 'block' : 'none'
                          }}
                        />
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ maxWidth: 300 }}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        className="form-control py-11"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-sm-6">
                      <label htmlFor="media" className="h5 mb-8 fw-semibold font-heading">
                        Media / Publication Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="media"
                        className="form-control py-11"
                        value={media}
                        onChange={(e) => setMedia(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="link" className="h5 mb-8 fw-semibold font-heading">
                        Coverage Link
                      </label>
                      <input
                        type="url"
                        id="link"
                        className="form-control py-11"
                        placeholder="https://..."
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12 flex-align justify-content-end gap-8">
                      <Link to="/admin-press-media" className="btn btn-outline-main rounded-pill py-9">
                        Cancel
                      </Link>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : 'Update Press & Media Item'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminEditPressMedia;
